/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export function ProductTileSwatchesSkeleton({ count = 2 }: { count?: number }) {
    return (
        <div className="product-tile-swatches-skeleton flex items-center flex-wrap gap-1 mb-2 relative z-20">
            {Array.from({ length: count }, (_, i) => (
                <Skeleton key={i} className="h-4 w-4 rounded-full" />
            ))}
        </div>
    );
}

export function ProductTileSkeleton() {
    return (
        <Card className="product-tile-skeleton overflow-hidden w-full min-w-0 max-w-full flex flex-col h-full gap-0 py-0">
            {/* Product image */}
            <CardHeader className="p-0">
                {/* RGBARRY-CUSTOMIZATION: match the 3:4 product tile image ratio (product-tile/index.tsx) so the
                    skeleton reserves the same height as a real tile — critical for back-nav scroll restoration
                    on "Load more" PLPs, where a shorter placeholder grid collapses the restored scroll position. */}
                <Skeleton className="aspect-[3/4] w-full" />
            </CardHeader>

            {/* Swatches */}
            <CardContent className="px-4 pt-3 pb-0">
                <ProductTileSwatchesSkeleton count={2} />
            </CardContent>

            {/* Product name */}
            <CardContent className="px-4 pt-2 pb-0">
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-3/4" />
            </CardContent>

            {/* Price */}
            <CardContent className="px-4 pt-2 pb-4">
                <Skeleton className="h-5 w-16" />
            </CardContent>
        </Card>
    );
}

/**
 * RGBARRY-CUSTOMIZATION: full-page PLP skeleton used as the category route's Suspense fallback
 * during instant-URL client navigations (replaces the former brand-logo loading screen). Mirrors
 * the real page's breadcrumb → heading → filter/sort bar → tile-grid structure and its exact
 * vertical rhythm (mb-4 / mb-8 / mb-4) so the grid lands in the same place when the real content
 * streams in — no layout jump. The filter/sort row is easy to forget: the live PLP reserves a
 * `h-9` FiltersButton + `mb-4` row above the grid, so omitting it drops the grid ~52px and the
 * page visibly shifts on reveal. `min-h-[60vh]` keeps the footer from riding up on short viewports.
 * The component deliberately owns no parent-layout compensation: callers inside the app shell's
 * padded `<main>` apply their own offset, while fixed overlays render it unchanged.
 */
export function CategoryGridSkeleton({ count = 12 }: { count?: number }) {
    return (
        <div className="pb-16" aria-hidden="true">
            <div className="section-container pt-8 min-h-[60vh]">
                {/* Breadcrumb — h-5 matches the live breadcrumb's measured 20px height */}
                <Skeleton className="h-5 w-40 mb-4" />
                {/* Heading + item count */}
                <Skeleton className="h-8 w-56 mb-8" />
                {/* Filter/sort bar — matches the live FiltersButton (h-9) + quick-filters + sort row
                    and its mb-4 so the grid below doesn't shift down when the real controls render. */}
                <div className="mb-4 flex items-center gap-4">
                    <Skeleton className="h-9 w-24" />
                    <Skeleton className="h-6 w-48 hidden sm:block" />
                    <Skeleton className="h-9 w-32 ml-auto hidden lg:block" />
                </div>
                {/* Product grid — matches the live grid columns/gaps */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-8">
                    {Array.from({ length: count }, (_, i) => (
                        <ProductTileSkeleton key={i} />
                    ))}
                </div>
            </div>
        </div>
    );
}
